import { memo, useMemo, useState } from 'react';

import { type PublicClip } from '@air/api';
import { itemIsHighlightedOnShiftSelectSelector } from '@air/redux-selected-items';

import { type AssetItemProps } from '~/components/Gallery/types';
import { AssetCellThumbnail } from '~/components/TableView/NameCell/AssetCellThumbnail';
import { NameCellContent } from '~/components/TableView/NameCell/NameCellContent';
import { DropdownMenuWrapper } from '~/components/TableView/PrivateTableView/DropdownMenuWrapper';
import { PublicAssetNameCellTitle } from '~/components/TableView/PrivateTableView/PublicAssetNameCellTitle';
import { ASSET_NAME_CELL } from '~/constants/testIDs';
import { useAirSelector } from '~/utils/ReduxUtils';

export type PublicAssetNameCellProps = {
  isHovering: boolean;
  isSelected: boolean;
  clip: PublicClip;
} & AssetItemProps;

export const PublicAssetNameCell = memo(
  ({
    isHovering,
    isSelected,
    clip,
    getItemMenuOptions,
    getSelectionMenuOptions,
    onItemClick,
    onItemCmdClick,
    onCommentsClick,
    onVersionsClick,
  }: PublicAssetNameCellProps) => {
    const itemTitle = clip.title || clip.importedName || 'Untitled';

    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const isHighlightedFromShiftSelect = useAirSelector((st) => itemIsHighlightedOnShiftSelectSelector(st, clip.id));

    const shouldShowMenu = !isHighlightedFromShiftSelect && (isHovering || menuIsOpen);

    const _title = useMemo(() => {
      return (
        <PublicAssetNameCellTitle
          id={clip.id}
          title={itemTitle}
          onClick={onItemClick}
          onCmdClick={onItemCmdClick}
          onCommentsClick={onCommentsClick}
          onVersionsClick={onVersionsClick}
          openCommentCount={clip.openCommentCount || 0}
          assetVersionCount={clip.assetVersionCount || 0}
          pagesCount={clip.pages || 0}
        />
      );
    }, [
      clip.pages,
      clip.assetVersionCount,
      clip.id,
      clip.openCommentCount,
      itemTitle,
      onCommentsClick,
      onItemClick,
      onItemCmdClick,
      onVersionsClick,
    ]);

    const _image = useMemo(() => {
      return <AssetCellThumbnail clip={clip} description={itemTitle} />;
    }, [clip, itemTitle]);

    const ellipsisMenuOptions = useMemo(
      () => getItemMenuOptions({ trackLocation: 'table-item-ellipsis-menu' }),
      [getItemMenuOptions],
    );

    const selectionMenuOptions = getSelectionMenuOptions('table-multi-select-ellipsis-menu');
    const menuOptions = isSelected && selectionMenuOptions.length ? selectionMenuOptions : ellipsisMenuOptions;

    return (
      <NameCellContent data-testid={ASSET_NAME_CELL} title={_title} image={_image}>
        {shouldShowMenu && !!menuOptions.length && (
          <DropdownMenuWrapper
            options={menuOptions}
            onOpenChange={setMenuIsOpen}
            dataTestId={`${ASSET_NAME_CELL}-menu`}
          />
        )}
      </NameCellContent>
    );
  },
);

PublicAssetNameCell.displayName = 'PublicAssetNameCell';
